import { Fragment, useState, useMemo } from 'react';
import {
  Tooltip,
  Divider,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
} from '@mui/material';
import { limitStringSize, getDimensionsString, formatDate } from '../../lib';
import { LongTextHoverTooltip } from '../../components';
import { PickNStickIcon, ExpandMoreIcon, InfoIcon } from '../../assets';
import PickAndStickSVG from '../../assets/images/pick-and-stick.svg';
import { useSelector, orderSelectors } from '../../state';
import { styles } from './ItemsTab.styles';

export function ItemsTab() {
  const itemsRemaining = useSelector(orderSelectors.itemsRemaining);
  const itemsCompleted = useSelector(orderSelectors.itemsCompleted);
  const selectedOrder = useSelector(orderSelectors.selectedOrder);

  const itemsRemainingKeys = Object.keys(itemsRemaining).filter(
    (key) => itemsRemaining[key].quantity > 0,
  );
  const itemsCompletedKeys = Object.keys(itemsCompleted);

  return (
    <Fragment>
      {itemsRemainingKeys.length ? (
        <RenderItems
          items={itemsRemaining}
          keys={itemsRemainingKeys}
          type='remaining'
          selectedOrder={selectedOrder}
        />
      ) : (
        <EmptyItemsRemaining />
      )}
      {itemsCompletedKeys.length ? (
        <RenderItems
          items={itemsCompleted}
          keys={itemsCompletedKeys}
          type='completed'
          selectedOrder={selectedOrder}
        />
      ) : (
        <EmptyItemsCompleted />
      )}
      <RenderPhysicalItems />
    </Fragment>
  );
}

function RenderItems({ items, keys, type, selectedOrder }) {
  const { classes, cx } = styles();
  const isReship = useSelector(orderSelectors.isReship);
  const isItemsRemaining = type === 'remaining';

  return (
    <div
      className={cx(classes.remainingContainer, {
        [classes.remainingBg]: isItemsRemaining,
      })}
    >
      <div className={classes.subHeader}>
        {getSectionTitle({ isItemsRemaining, isReship })}
      </div>
      <div className={classes.visibleSidebarContainer}>
        {keys.map((item, index) => {
          const {
            quantity,
            sku,
            description,
            lengthInches,
            heightInches,
            widthInches,
            weightPounds,
            hasEverPickedAndSticked,
            isPrimaryUnitPallet,
            binIDs,
            additionalPallets,
          } = items[item];

          const isPickAndShipItem =
            selectedOrder.isFreight &&
            !selectedOrder.defaultShipsWholePallet &&
            isPrimaryUnitPallet;

          const bin =
            isItemsRemaining && !!binIDs?.length
              ? `This item may be on bin(s) ${binIDs.join(', ')}`
              : '';

          return (
            <Fragment key={sku}>
              <div className={classes.itemContainer}>
                <span className={classes.itemQty}>{quantity}</span>
                <div className={classes.itemDetails}>
                  <div className={classes.skuContainer}>
                    <span className={classes.itemSku}>{sku}</span>
                    {isPickAndShipItem && (
                      <PickAndStickSVG className={classes.palletImg} />
                    )}
                  </div>
                  <div>
                    <Tooltip title={description}>
                      <span className={classes.itemDetailsItem}>
                        {limitStringSize(description, 15, true)}
                      </span>
                    </Tooltip>
                    <span className={classes.itemDetailsItem}>|</span>
                    <span className={classes.itemDetailsItem}>
                      {getDimensionsString({
                        length: lengthInches,
                        width: widthInches,
                        height: heightInches,
                      })}
                    </span>
                    <span className={classes.itemDetailsItem}>|</span>
                    <span className={classes.itemDetailsItem}>{`${
                      weightPounds ?? '-'
                    } lbs`}</span>
                  </div>
                </div>
                {!!bin && (
                  <Tooltip title={bin}>
                    <div className={classes.bin}>
                      {limitStringSize(bin, 5, true)}
                    </div>
                  </Tooltip>
                )}
                {isItemsRemaining && hasEverPickedAndSticked && (
                  <PickNStickIcon className={classes.remainingPickNStickIcon} />
                )}
                {isItemsRemaining && (
                  <AdditionalPalletsInfo
                    additionalPallets={additionalPallets}
                  />
                )}
              </div>
              {index < Object.keys(items).length - 1 && (
                <Divider className={classes.itemDivider} />
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

function RenderPhysicalItems() {
  const { classes } = styles();

  const physicalItems = useSelector(orderSelectors.physicalItems);
  const physicalItemsKeys = useMemo(
    () => (physicalItems ? Object.keys(physicalItems) : []),
    [physicalItems],
  );

  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <Accordion
        elevation={0}
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        slotProps={{ transition: { unmountOnExit: true } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel1a-content'
          id='panel1a-header'
        >
          <div
            className={classes.subHeader}
            style={{ marginBottom: 0, paddingLeft: 8 }}
          >
            Allocated inventory
          </div>
        </AccordionSummary>
        <AccordionDetails
          style={{ paddingTop: 0, paddingRight: 24, paddingLeft: 24 }}
        >
          <div style={{ width: '100%' }}>
            {!!physicalItemsKeys?.length ? (
              <div>
                {physicalItemsKeys.map((sku, i) => {
                  const itemsPerSku = physicalItems[sku];
                  if (!itemsPerSku?.length) return null;

                  return (
                    <div key={sku} style={{ marginTop: i > 0 ? 8 : 0 }}>
                      <p className={classes.skuHeader}>{`Item: ${sku}`}</p>
                      <Grid container style={{ marginBottom: 8 }}>
                        <Grid
                          className={classes.physicalItemsHead}
                          style={{ textAlign: 'start' }}
                          size={{
                            sm: 3,
                          }}
                        >
                          Qty
                        </Grid>
                        <Grid
                          className={classes.physicalItemsHead}
                          style={{ textAlign: 'start' }}
                          size={{
                            sm: 3,
                          }}
                        >
                          Serial
                        </Grid>
                        <Grid
                          className={classes.physicalItemsHead}
                          style={{ textAlign: 'center' }}
                          size={{
                            sm: 3,
                          }}
                        >
                          Lot
                        </Grid>
                        <Grid
                          className={classes.physicalItemsHead}
                          style={{ textAlign: 'end' }}
                          size={{
                            sm: 3,
                          }}
                        >
                          Expiration
                        </Grid>
                      </Grid>
                      <Grid container>
                        {itemsPerSku.map((i) => {
                          const {
                            id,
                            serialNumber,
                            lotNumber,
                            importedLotNumber,
                            importedExpirationDate,
                            expirationDate,
                            quantity,
                          } = i;
                          return (
                            <Fragment key={id}>
                              <Grid
                                className={classes.physicalItem}
                                style={{ textAlign: 'start' }}
                                size={{
                                  sm: 3,
                                }}
                              >
                                {quantity ?? '--'}
                              </Grid>
                              <Grid
                                className={classes.physicalItem}
                                style={{ textAlign: 'start' }}
                                size={{
                                  sm: 3,
                                }}
                              >
                                <LongTextHoverTooltip
                                  text={serialNumber || '--'}
                                  maxLength={15}
                                />
                              </Grid>
                              <Grid
                                className={classes.physicalItem}
                                style={{ textAlign: 'center' }}
                                size={{
                                  sm: 3,
                                }}
                              >
                                <LongTextHoverTooltip
                                  text={importedLotNumber || lotNumber || '--'}
                                  maxLength={15}
                                />
                              </Grid>
                              <Grid
                                className={classes.physicalItem}
                                style={{ textAlign: 'end' }}
                                size={{
                                  sm: 3,
                                }}
                              >
                                {formatDate(
                                  importedExpirationDate || expirationDate,
                                ) || '--'}
                              </Grid>
                            </Fragment>
                          );
                        })}
                      </Grid>
                      {i < physicalItemsKeys.length - 1 && (
                        <Divider className={classes.itemDivider} />
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyPhysicalItems />
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

function EmptyItemsRemaining() {
  const { classes, cx } = styles();
  const isReship = useSelector(orderSelectors.isReship);

  return (
    <div className={cx(classes.remainingContainer, classes.remainingBg)}>
      <div className={classes.subHeader}>
        {getSectionTitle({ isItemsRemaining: true, isReship })}
      </div>
      <div className={classes.emptyText}>
        <div>There are no items</div>
        <div>remaining in this order</div>
      </div>
    </div>
  );
}

function EmptyItemsCompleted() {
  const isReship = useSelector(orderSelectors.isReship);

  const { classes } = styles();
  return (
    <div className={classes.remainingContainer}>
      <div className={classes.subHeader}>
        {getSectionTitle({ isItemsRemaining: false, isReship })}
      </div>
      <div className={classes.emptyText}>
        <div>As you scan items they will </div>
        <div>appear here</div>
      </div>
    </div>
  );
}

function EmptyPhysicalItems() {
  const { classes, css, cx } = styles();
  return (
    <div className={classes.remainingContainer}>
      <div className={cx(classes.emptyText, css({ padding: 0 }))}>
        <div>There are no allocation details </div>
        <div>for this order</div>
      </div>
    </div>
  );
}

function AdditionalPalletsInfo({ additionalPallets }) {
  const { classes } = styles();

  if (!additionalPallets?.length) return null;

  const tooltip = `This item may be found on pallet(s) ${additionalPallets
    .map(
      (p) =>
        `#${p.moveablePalletID} ${p.workstation ? `at ${p.workstation}` : ''}`,
    )
    .join(', ')}`;

  return (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={tooltip} classes={{ tooltip: classes.tooltip }}>
        <InfoIcon className={classes.infoIcon} />
      </Tooltip>
    </span>
  );
}

function getSectionTitle({ isItemsRemaining, isReship }) {
  if (isReship) {
    return `Items ${isItemsRemaining ? 'ordered' : 'reshipped'}`;
  } else {
    return `Items ${isItemsRemaining ? 'remaining' : 'completed'}`;
  }
}
